import { HD_URL, DEFAULT_ALTERNATE_LINKS, HD_LOGO } from '../../constants';

/* --- Schema & Meta Tag Helpers --- */

export const buildSearchSchema = () => ({
  '@context': 'http://schema.org',
  '@type': 'WebSite',
  url: HD_URL,
  potentialAction: {
    '@type': 'SearchAction',
    target: `${HD_URL}/s/{search_term_string}?NCNI-5&gsitesearch`,
    'query-input': 'required name=search_term_string'
  }
});

export const buildOrganizationSchema = () => ({
  '@context': 'http://schema.org',
  '@type': 'Organization',
  name: 'The Home Depot',
  alternateName: 'Home Depot',
  url: HD_URL,
  logo: HD_LOGO,
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '1-800-466-3337',
    contactType: 'customer service'
  },
  sameAs: [
    'https://www.facebook.com/homedepot/',
    'https://twitter.com/HomeDepot',
    'https://www.instagram.com/homedepot/',
    'https://www.youtube.com/homedepot',
    'https://www.linkedin.com/company/the-home-depot',
    'https://www.pinterest.com/homedepot/'
  ]
});

// Mirrors @thd-olt-component-react/hero's akamaiSrcSet / akamaiSrcSetDesktop EXACTLY —
// source: src/utils/akamaiImageOptimizer.js, consumed in
// src/components/bento-hero/sub-components/BayImage.jsx. Kept in sync so each preload
// resolves to the same URL the component's <img> (mobile) or <source> (desktop) selects —
// no duplicate download. Appends `imwidth` while preserving any existing query params.
const AKAMAI_TIERS = [320, 640, 1024];
const DESKTOP_TIERS = [1024, 2048];
const ORIGINAL_SRCSET_WIDTH = 3840;
const isAkamai = (url) => !!url && url.includes('thdstatic.com') && !url.split('?')[0].endsWith('.gif');
const buildImwidthSrc = (url, width) => `${url}${url.includes('?') ? '&' : '?'}imwidth=${width}`;
const akamaiSrcSet = (url) => (
  isAkamai(url) ? AKAMAI_TIERS.map((width) => `${buildImwidthSrc(url, width)} ${width}w`).join(', ') : undefined
);
const akamaiSrcSetDesktop = (url) => {
  if (!isAkamai(url)) return undefined;
  const tiers = DESKTOP_TIERS.map((width) => `${buildImwidthSrc(url, width)} ${width}w`);
  return [...tiers, `${url} ${ORIGINAL_SRCSET_WIDTH}w`].join(', ');
};

export const buildPreloadImages = (preloadedImages) => (preloadedImages || []).map(
  ({ preloadedImageUrl, preloadedImageId, media, fetchpriority } = {}) => {
    // Match each preload to what the component actually renders
    //   - Mobile previews (ids contain "Small") render the mobile imwidth srcset at 100vw.
    //   - Desktop previews (ids contain "Large") render the desktop srcset (1024/2048/orig).
    //   - Video posters (neither) render a raw URL, so they stay plain href preloads.
    const preloadId = preloadedImageId || '';
    const isMobilePreview = preloadId.includes('Small');
    const isDesktopPreview = preloadId.includes('Large');
    let imagesrcset;
    let href = preloadedImageUrl;
    if (isMobilePreview) {
      imagesrcset = akamaiSrcSet(preloadedImageUrl);
      if (imagesrcset) href = buildImwidthSrc(preloadedImageUrl, 1024); // matches the mobile <img> src
    } else if (isDesktopPreview) {
      imagesrcset = akamaiSrcSetDesktop(preloadedImageUrl);
      if (imagesrcset) href = buildImwidthSrc(preloadedImageUrl, 2048); // matches the desktop <source> high tier
    }
    return {
      rel: 'preload',
      as: 'image',
      href,
      id: preloadedImageId,
      ...(media ? { media } : {}),
      ...(imagesrcset ? { imagesrcset, imagesizes: '100vw' } : {}),
      fetchpriority: fetchpriority || 'high'
    };
  }
);

export const buildAlternateLinks = (alternate) => {
  const links = (alternate?.length ? alternate : DEFAULT_ALTERNATE_LINKS)
    .map(({ href, hreflang }) => {
      if (!href) {
        return null;
      }
      return {
        href,
        hreflang,
        id: `alternate-${href}`
      };
    })
    .filter(Boolean);

  // Always add Android app link
  links.push({
    href: 'android-app://com.thehomedepot/homedepot/view/homepage',
    id: 'alternate-android-app://com.thehomedepot/homedepot/view/homepage'
  });

  return links;
};